import React, { useEffect, useMemo, useState } from "react";
import { api, assetUrl } from "../lib/api";
import { toast } from "sonner";
import { Sparkles, Download, Copy, RefreshCw, Hash, Image as ImageIcon, ChevronRight, Check, Type } from "lucide-react";

const STYLES_FALLBACK = {
  marketplace: { label: "🛒 Marketplace", desc: "Vente / promo annonce" },
  drop:        { label: "🔥 Drop / Sortie", desc: "Nouveau son ou clip qui sort" },
  hype:        { label: "👀 Hype / Teaser", desc: "Teaser mystère" },
  street:      { label: "🎤 Street / Rap",  desc: "Citation rap, attitude" },
  event:       { label: "📍 Event / Tour",  desc: "Concert, soirée, date" },
};

const TYPE_TABS = [
  { key: "annonce",    label: "🛒 Annonce" },
  { key: "son",        label: "🎵 Son" },
  { key: "clip",       label: "📺 Clip" },
  { key: "story-text", label: "✍️ Texte" },
];

export const AdminTikTokStudioPanel = () => {
  const [sources, setSources] = useState({ ads: [], sounds: [], videos: [], brand: {}, styles: STYLES_FALLBACK });
  const [type, setType] = useState("annonce");
  const [sourceId, setSourceId] = useState(null);
  const [style, setStyle] = useState("marketplace");
  const [customText, setCustomText] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copiedIdx, setCopiedIdx] = useState(-1);

  useEffect(() => {
    api.get("/admin/arnak/tiktok-studio/sources")
      .then(({ data }) => setSources({ ...data, styles: data.styles || STYLES_FALLBACK }))
      .catch(() => toast.error("Erreur chargement sources"));
  }, []);

  // Reset source/style auto en changeant de type
  useEffect(() => {
    setSourceId(null);
    setResult(null);
    if (type === "annonce") setStyle("marketplace");
    else if (type === "son" || type === "clip") setStyle("drop");
    else if (type === "story-text") setStyle("street");
  }, [type]);

  const list = useMemo(() => {
    if (type === "annonce") return sources.ads || [];
    if (type === "son")     return sources.sounds || [];
    if (type === "clip")    return sources.videos || [];
    return [];
  }, [type, sources]);

  const generate = async () => {
    if (type !== "story-text" && !sourceId) {
      toast.error("Choisis une source");
      return;
    }
    if (type === "story-text" && !customText.trim()) {
      toast.error("Écris ton texte");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/admin/arnak/tiktok-studio/generate", {
        type,
        source_id: sourceId,
        style,
        custom_text: customText.trim() || undefined,
      });
      setResult(data);
      toast.success("Visuel généré !");
    } catch (e) {
      toast.error(e?.response?.data?.error || "Erreur génération");
    } finally {
      setLoading(false);
    }
  };

  const copyText = async (txt, idx) => {
    try {
      await navigator.clipboard.writeText(txt);
      setCopiedIdx(idx);
      toast.success("Légende copiée");
      setTimeout(() => setCopiedIdx(-1), 1800);
    } catch { toast.error("Copie impossible"); }
  };

  const copyHashtags = async () => {
    if (!result?.hashtags) return;
    try {
      await navigator.clipboard.writeText(result.hashtags.join(" "));
      toast.success("Hashtags copiés");
    } catch { toast.error("Copie impossible"); }
  };

  const downloadImage = () => {
    if (!result?.image_url) return;
    const url = assetUrl(result.image_url);
    const a = document.createElement("a");
    a.href = url;
    a.download = "arnak-tiktok-" + Date.now() + ".jpg";
    a.target = "_blank";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <div className="space-y-6" data-testid="tiktok-studio-panel">
      <div>
        <h2 className="font-black text-2xl mb-1 flex items-center gap-2" style={{ fontFamily: "'Anton', sans-serif" }}>
          <Sparkles size={22} className="text-yellow-400" /> Studio TikTok
        </h2>
        <p className="text-xs text-white/55">
          Génère un visuel <strong>9:16 (1080×1920)</strong> + un pack de 5 légendes optimisées TikTok à partir d'une annonce, d'un son, d'un clip ou d'un texte libre.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* ─── Colonne gauche : sélection ─── */}
        <div className="space-y-4">
          {/* Type */}
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-white/55 font-bold mb-2">1. Type de visuel</label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {TYPE_TABS.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setType(t.key)}
                  className={`px-3 py-2.5 rounded-lg text-xs font-black uppercase tracking-widest transition border ${
                    type === t.key
                      ? "bg-yellow-400 text-black border-yellow-400"
                      : "bg-white/[0.04] hover:bg-white/[0.08] text-white/75 border-white/10 hover:border-white/25"
                  }`}
                  data-testid={`tts-type-${t.key}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Source */}
          {type !== "story-text" ? (
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-white/55 font-bold mb-2">
                2. Choisir la source ({list.length})
              </label>
              {list.length === 0 ? (
                <p className="text-sm text-white/45 italic">Aucune source disponible pour ce type.</p>
              ) : (
                <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1 -mr-1">
                  {list.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => setSourceId(s.id)}
                      className={`w-full flex items-center gap-3 p-2.5 rounded-lg border transition text-left ${
                        sourceId === s.id
                          ? "bg-yellow-400/15 border-yellow-400/60"
                          : "bg-white/[0.03] hover:bg-white/[0.07] border-white/10 hover:border-white/25"
                      }`}
                      data-testid={`tts-source-${s.id}`}
                    >
                      <div className="w-14 h-14 rounded bg-zinc-900 shrink-0 overflow-hidden flex items-center justify-center">
                        {(s.photos?.[0] || s.thumbnail_url) ? (
                          <img src={assetUrl(s.photos?.[0] || s.thumbnail_url)} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <ImageIcon size={20} className="text-white/20" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-bold text-sm leading-tight truncate">{s.title}</p>
                        <p className="text-[10px] text-white/45 truncate">
                          {s.city && <>{s.city}</>}
                          {s.city && (s.price_amount || s.price_type) && " · "}
                          {s.price_type === "fixed" && s.price_amount ? `${s.price_amount.toLocaleString("fr-FR")} Ar` : (s.price_type === "free" ? "Gratuit" : (s.price_type === "negotiable" ? "À débattre" : ""))}
                        </p>
                      </div>
                      {sourceId === s.id && <Check size={16} className="text-yellow-400 shrink-0" />}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-white/55 font-bold mb-2">
                2. Ton texte (citation, punchline, annonce…)
              </label>
              <textarea
                value={customText}
                onChange={(e) => setCustomText(e.target.value)}
                placeholder="Ex: ON LÂCHE RIEN — ARNAK 110"
                maxLength={120}
                rows={3}
                className="w-full px-3 py-2.5 bg-white/[0.04] border border-white/10 rounded-lg text-sm focus:border-yellow-400 focus:outline-none resize-none"
                data-testid="tts-custom-text"
              />
              <p className="text-[10px] text-white/40 mt-1">{customText.length}/120 caractères</p>
            </div>
          )}

          {/* Style */}
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-white/55 font-bold mb-2">3. Style visuel</label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {Object.entries(sources.styles).map(([k, v]) => (
                <button
                  key={k}
                  onClick={() => setStyle(k)}
                  className={`px-3 py-2.5 rounded-lg text-xs font-bold transition border ${
                    style === k
                      ? "bg-yellow-400/20 text-yellow-300 border-yellow-400/60"
                      : "bg-white/[0.03] hover:bg-white/[0.08] text-white/75 border-white/10 hover:border-white/25"
                  }`}
                  data-testid={`tts-style-${k}`}
                >
                  {v.label}
                </button>
              ))}
            </div>
            <p className="text-[10px] text-white/40 mt-1.5">{sources.styles[style]?.desc}</p>
          </div>

          {/* Bouton générer */}
          <button
            onClick={generate}
            disabled={loading || (type !== "story-text" && !sourceId) || (type === "story-text" && !customText.trim())}
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-yellow-400 hover:bg-yellow-300 disabled:opacity-40 disabled:cursor-not-allowed text-black text-sm font-black uppercase tracking-widest transition shadow-lg shadow-yellow-400/20"
            data-testid="tts-generate-btn"
          >
            {loading ? <RefreshCw size={16} className="animate-spin" /> : <Sparkles size={16} />}
            {loading ? "Génération…" : "Générer le visuel"}
          </button>
        </div>

        {/* ─── Colonne droite : aperçu + légendes ─── */}
        <div className="space-y-4">
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-white/55 font-bold mb-2">Aperçu 9:16</label>
            <div className="bg-zinc-900 border border-white/10 rounded-2xl overflow-hidden flex items-center justify-center" style={{ aspectRatio: "9/16", maxHeight: "600px" }}>
              {loading ? (
                <RefreshCw size={32} className="animate-spin text-yellow-400" />
              ) : result?.image_url ? (
                <img src={assetUrl(result.image_url)} alt="Aperçu TikTok" className="max-w-full max-h-full object-contain" data-testid="tts-preview-img" />
              ) : (
                <div className="text-center text-white/30 px-6">
                  <ImageIcon size={48} className="mx-auto mb-2" />
                  <p className="text-sm">Le visuel apparaîtra ici</p>
                </div>
              )}
            </div>
            {result?.image_url && (
              <button
                onClick={downloadImage}
                className="w-full mt-3 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-white/10 hover:bg-white/20 text-sm font-black uppercase tracking-widest transition"
                data-testid="tts-download-btn"
              >
                <Download size={14} /> Télécharger l'image JPG
              </button>
            )}
          </div>

          {result?.captions && (
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-white/55 font-bold mb-2 flex items-center gap-2">
                <Type size={11} /> Légendes prêtes à copier-coller
              </label>
              <div className="space-y-2">
                {result.captions.map((c, i) => (
                  <button
                    key={i}
                    onClick={() => copyText(c + "\n\n" + result.hashtags.slice(0, 8).join(" "), i)}
                    className="w-full text-left p-3 bg-white/[0.03] hover:bg-white/[0.08] border border-white/10 hover:border-yellow-400/40 rounded-lg transition group"
                    data-testid={`tts-caption-${i}`}
                  >
                    <p className="text-xs text-white/85 whitespace-pre-wrap leading-relaxed">{c}</p>
                    <p className="text-[10px] text-yellow-300/70 mt-1.5 inline-flex items-center gap-1">
                      {copiedIdx === i ? <><Check size={10} /> Copié avec hashtags !</> : <><Copy size={10} /> Cliquer pour copier (avec hashtags)</>}
                    </p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {result?.hashtags && (
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-white/55 font-bold mb-2 flex items-center gap-2">
                <Hash size={11} /> Hashtags ({result.hashtags.length})
              </label>
              <div className="p-3 bg-white/[0.03] border border-white/10 rounded-lg">
                <p className="text-xs text-yellow-300/90 leading-relaxed break-words">{result.hashtags.join(" ")}</p>
                <button
                  onClick={copyHashtags}
                  className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-[10px] font-black uppercase tracking-widest bg-white/10 hover:bg-white/20 transition"
                  data-testid="tts-copy-hashtags"
                >
                  <Copy size={11} /> Copier tous
                </button>
              </div>
            </div>
          )}

          {result?.cta && (
            <p className="text-xs text-white/55 italic">💡 {result.cta}</p>
          )}
        </div>
      </div>

      <div className="text-xs text-white/45 bg-white/[0.02] border border-white/10 rounded-lg p-4">
        <p className="font-bold text-white/65 mb-1">📌 Comment utiliser :</p>
        <ol className="list-decimal pl-5 space-y-0.5">
          <li>Choisis le type de visuel et la source (annonce, son, clip ou texte libre)</li>
          <li>Choisis le style qui colle au ton voulu</li>
          <li>Génère, télécharge l'image, copie une légende</li>
          <li>Va sur TikTok mobile → publier → upload l'image générée → colle la légende → poste 🚀</li>
        </ol>
      </div>
    </div>
  );
};

export default AdminTikTokStudioPanel;
